<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="sms-page">
    <div class="page-header">
      <div class="page-header-text">
        <h1 class="page-title">SMS</h1>
      </div>
    </div>
    <v-tabs v-model="activeTab" background-color="transparent" class="mb-4 sms-tabs">
      <v-tab>
        <v-icon left>mdi-send</v-icon>
        {{ $t('sms.sendSms') }}
      </v-tab>
      <v-tab>
        <v-icon left>mdi-history</v-icon>
        {{ $t('sms.sendHistory') }}
      </v-tab>
      <v-tab>
        <v-icon left>mdi-message-reply</v-icon>
        {{ $t('sms.replies') }}
        <v-badge v-if="unreadRepliesCount > 0" :content="unreadRepliesCount" color="red" class="ml-2" />
      </v-tab>
      <v-tab>
        <v-icon left>mdi-clock-outline</v-icon>
        Zaplanowane
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab" touchless>
      <!-- ==================== TAB 1: SEND SMS ==================== -->
      <v-tab-item>
        <v-card outlined class="sms-composer-card">
          <div class="sms-composer-hero">
            <div class="sms-composer-hero__icon">
              <v-icon color="white">mdi-message-processing-outline</v-icon>
            </div>
            <div>
              <div class="sms-composer-eyebrow">{{ $t('sms.composerEyebrow') }}</div>
              <h2>{{ $t('sms.composerTitle') }}</h2>
              <p>{{ $t('sms.composerSubtitle') }}</p>
            </div>
            <div class="sms-live-pill">
              <span class="sms-live-pill__dot"></span>
              {{ $t('sms.livePreview') }}
            </div>
          </div>

          <v-card-text class="sms-composer-body">
            <v-row>
              <v-col cols="12" lg="4">
                <section class="sms-section-card sms-recipient-card">
                  <div class="sms-section-heading">
                    <span class="sms-section-step">1</span>
                    <div>
                      <h3>{{ $t('sms.chooseRecipients') }}</h3>
                      <p>{{ $t('sms.chooseRecipientsHint') }}</p>
                    </div>
                  </div>

                  <v-btn-toggle
                    v-model="sendType"
                    mandatory
                    class="sms-mode-toggle"
                  >
                    <v-btn value="SINGLE">
                      <v-icon left small>mdi-account-outline</v-icon>
                      {{ $t('sms.singlePerson') }}
                    </v-btn>
                    <v-btn value="BULK">
                      <v-icon left small>mdi-account-multiple-outline</v-icon>
                      {{ $t('sms.bulkSend') }}
                    </v-btn>
                  </v-btn-toggle>

                  <!-- ===== SINGLE MODE ===== -->
                  <template v-if="sendType === 'SINGLE'">
                    <v-autocomplete
                      v-model="singleForm.recipient"
                      :items="recipientSearchResults"
                      :search-input.sync="recipientSearch"
                      :loading="searchingRecipients"
                      item-text="name"
                      item-value="id"
                      return-object
                      :label="$t('sms.recipient')"
                      prepend-inner-icon="mdi-account-search-outline"
                      :placeholder="$t('sms.searchByNameOrPhone')"
                      :no-data-text="$t('sms.noResults')"
                      clearable
                      outlined
                      @change="onRecipientSelected"
                    >
                      <template v-slot:item="{ item }">
                        <v-list-item-content>
                          <v-list-item-title>{{ item.name }}</v-list-item-title>
                          <v-list-item-subtitle>{{ item.phone }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                    </v-autocomplete>

                    <v-text-field
                      v-model="singleForm.phone"
                      :label="$t('sms.phoneNumber')"
                      prepend-inner-icon="mdi-phone-outline"
                      outlined
                      :rules="[v => !!v || $t('sms.phoneRequired')]"
                    ></v-text-field>
                  </template>

                  <!-- ===== BULK MODE ===== -->
                  <template v-else>
                    <v-select
                      v-model="bulkForm.filterType"
                      :items="bulkFilterOptions"
                      :label="$t('sms.targetGroup')"
                      prepend-inner-icon="mdi-account-group-outline"
                      outlined
                      @change="previewBulkRecipients"
                    ></v-select>

                    <v-select
                      v-if="bulkForm.filterType === 'DEPARTMENT'"
                      v-model="bulkForm.departmentId"
                      :items="departments"
                      item-text="name"
                      item-value="id"
                      :label="$t('sms.department')"
                      prepend-inner-icon="mdi-office-building-outline"
                      outlined
                      @change="previewBulkRecipients"
                    ></v-select>

                    <!-- Kryteria: lacza sie przez AND, np. Miesiecznie + Gotowka -->
                    <template v-if="bulkForm.filterType === 'CRITERIA'">
                      <v-select
                        v-model="bulkForm.payoutSchedules"
                        :items="payoutScheduleOptions"
                        label="Harmonogram wyplat"
                        prepend-inner-icon="mdi-calendar-clock"
                        multiple
                        chips
                        deletable-chips
                        outlined
                        @change="previewBulkRecipients"
                      ></v-select>

                      <v-select
                        v-model="bulkForm.payoutTypes"
                        :items="payoutTypeOptions"
                        label="Tryb wyplaty"
                        prepend-inner-icon="mdi-cash-multiple"
                        multiple
                        chips
                        deletable-chips
                        outlined
                        @change="previewBulkRecipients"
                      ></v-select>

                      <v-select
                        v-model="bulkForm.cooperationTypes"
                        :items="cooperationTypeOptions"
                        label="Typ wspolpracy"
                        prepend-inner-icon="mdi-handshake-outline"
                        multiple
                        chips
                        deletable-chips
                        outlined
                        @change="previewBulkRecipients"
                      ></v-select>

                      <v-alert
                        v-if="!hasAnyCriterion"
                        type="info"
                        dense
                        text
                        class="mb-4"
                      >
                        Wybierz co najmniej jedno kryterium. Zaznaczenie kilku
                        wartosci w jednym polu oznacza "dowolna z nich", a pola
                        lacza sie ze soba (np. Miesiecznie + Gotowka).
                      </v-alert>
                    </template>

                    <v-autocomplete
                      v-if="bulkForm.filterType === 'MANUAL'"
                      v-model="bulkForm.manualRecipients"
                      :items="recipientSearchResults"
                      :search-input.sync="recipientSearch"
                      :loading="searchingRecipients"
                      item-text="name"
                      item-value="id"
                      return-object
                      :label="$t('sms.selectPeople')"
                      prepend-inner-icon="mdi-account-multiple-plus-outline"
                      multiple
                      chips
                      deletable-chips
                      outlined
                      :no-data-text="$t('sms.typeToSearch')"
                    >
                      <template v-slot:item="{ item }">
                        <v-list-item-content>
                          <v-list-item-title>{{ item.name }}</v-list-item-title>
                          <v-list-item-subtitle>{{ item.phone }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                    </v-autocomplete>
                  </template>

                  <div class="sms-recipient-summary" :class="{ 'sms-recipient-summary--empty': recipientCount === 0 }">
                    <div class="sms-recipient-summary__icon">
                      <v-icon small>mdi-account-check-outline</v-icon>
                    </div>
                    <div>
                      <span>{{ $t('sms.readyRecipients') }}</span>
                      <strong>{{ recipientCount }}</strong>
                    </div>
                  </div>
                </section>
              </v-col>

              <v-col cols="12" lg="8">
                <section class="sms-section-card sms-message-card">
                  <div class="sms-section-heading">
                    <span class="sms-section-step">2</span>
                    <div>
                      <h3>{{ $t('sms.writeMessage') }}</h3>
                      <p>{{ $t('sms.writeMessageHint') }}</p>
                    </div>
                  </div>

                  <v-row class="sms-editor-row">
                    <v-col cols="12" md="7">
                      <div class="sms-quick-actions">
                        <span>{{ $t('sms.quickInsert') }}</span>
                        <v-btn x-small outlined @click="addGreeting">
                          <v-icon left x-small>mdi-hand-wave-outline</v-icon>
                          {{ $t('sms.greeting') }}
                        </v-btn>
                        <v-btn x-small outlined @click="addParagraph">
                          <v-icon left x-small>mdi-format-paragraph</v-icon>
                          {{ $t('sms.newParagraph') }}
                        </v-btn>
                        <v-btn x-small outlined @click="addSignature">
                          <v-icon left x-small>mdi-fountain-pen-tip</v-icon>
                          {{ $t('sms.btpSignature') }}
                        </v-btn>
                      </div>

                      <v-switch
                        v-model="stripDiacritics"
                        dense
                        inset
                        hide-details
                        class="sms-strip-switch"
                        :label="$t('sms.stripPolish')"
                      ></v-switch>

                      <v-textarea
                        ref="smsMessageInput"
                        v-model="smsForm.message"
                        :label="$t('sms.smsMessage')"
                        :placeholder="$t('sms.messagePlaceholder')"
                        class="sms-message-input"
                        outlined
                        counter="320"
                        maxlength="320"
                        :rules="messageRules"
                        rows="9"
                        no-resize
                      ></v-textarea>

                      <div class="sms-metrics">
                        <div class="sms-metric">
                          <span>{{ $t('sms.characters') }}</span>
                          <strong>{{ smsMetrics.characters }}<small>/320</small></strong>
                        </div>
                        <div class="sms-metric">
                          <span>{{ $t('sms.smsParts') }}</span>
                          <strong>{{ smsMetrics.parts }}</strong>
                        </div>
                        <div class="sms-metric">
                          <span>{{ $t('sms.encoding') }}</span>
                          <strong>{{ smsMetrics.encoding }}</strong>
                        </div>
                      </div>

                      <div class="sms-segment-progress">
                        <div>
                          <span>{{ $t('sms.currentPart') }}</span>
                          <span>{{ smsMetrics.usedInPart }}/{{ smsMetrics.partLimit }}</span>
                        </div>
                        <v-progress-linear
                          :value="smsMetrics.progress"
                          :color="smsMetrics.isUnicode ? 'warning' : 'primary'"
                          height="6"
                          rounded
                        ></v-progress-linear>
                        <small>{{ $t('sms.remainingInPart', { count: smsMetrics.remaining }) }}</small>
                      </div>

                      <v-alert
                        v-if="smsMetrics.isUnicode && smsMetrics.characters"
                        dense
                        text
                        type="warning"
                        class="sms-unicode-alert"
                      >
                        {{ $t('sms.unicodeHint') }}
                      </v-alert>

                      <v-checkbox
                        v-model="smsForm.allowReply"
                        :label="$t('sms.allowReply')"
                        hide-details
                        class="sms-reply-checkbox"
                      ></v-checkbox>
                    </v-col>

                    <v-col cols="12" md="5">
                      <div class="sms-phone-preview">
                        <div class="sms-phone-preview__top">
                          <span>9:41</span>
                          <div class="sms-phone-preview__speaker"></div>
                          <v-icon x-small color="#dfe6f6">mdi-signal-cellular-3</v-icon>
                        </div>
                        <div class="sms-phone-preview__contact">
                          <div class="sms-phone-preview__avatar">BTP</div>
                          <strong>BTP</strong>
                          <span>{{ $t('sms.previewLabel') }}</span>
                        </div>
                        <div class="sms-phone-preview__screen">
                          <div
                            class="sms-phone-preview__bubble sms-content-preserved"
                            :class="{ 'sms-phone-preview__bubble--empty': !messageForSend }"
                          >{{ messageForSend || $t('sms.emptyPreview') }}</div>
                          <span v-if="messageForSend" class="sms-phone-preview__time">
                            {{ $t('sms.justNow') }}
                          </span>
                        </div>
                        <div class="sms-phone-preview__home"></div>
                      </div>
                    </v-col>
                  </v-row>
                </section>
              </v-col>
            </v-row>

            <div class="sms-actions">
              <div class="sms-actions__summary">
                <v-icon small color="#78a0ff">mdi-information-outline</v-icon>
                <span v-if="sendType === 'BULK' && recipientCount && smsMetrics.parts">
                  {{ $t('sms.totalEstimate', { recipients: recipientCount, parts: smsMetrics.parts, total: recipientCount * smsMetrics.parts }) }}
                </span>
                <span v-else>{{ $t('sms.formattingPreserved') }}</span>
              </div>
              <div class="sms-actions__buttons">
                <v-btn text large @click="confirmClearForm">{{ $t('sms.clear') }}</v-btn>
                <v-btn text large @click="openTemplatesDialog">
                  <v-icon left>mdi-text-box-multiple-outline</v-icon>
                  {{ $t('sms.templates') }}
                </v-btn>
                <v-btn
                  v-if="sendType === 'BULK'"
                  color="primary"
                  outlined
                  large
                  :disabled="!canSendBulk"
                  @click="openScheduleDialog"
                >
                  <v-icon left>mdi-clock-plus-outline</v-icon>
                  {{ $t('sms.schedule') }}
                </v-btn>
                <v-btn
                  v-if="sendType === 'SINGLE'"
                  color="primary"
                  :loading="sending"
                  :disabled="!canSendSingle"
                  @click="sendSingle"
                  large
                  class="sms-primary-action"
                >
                  <v-icon left>mdi-send</v-icon>
                  {{ $t('sms.send') }}
                </v-btn>
                <v-btn
                  v-else
                  color="primary"
                  :loading="sending"
                  :disabled="!canSendBulk"
                  @click="confirmBulkSend"
                  large
                  class="sms-primary-action"
                >
                  <v-icon left>mdi-send</v-icon>
                  {{ $t('sms.sendTo') }} {{ recipientCount }} {{ $t('sms.people') }}
                </v-btn>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- ==================== TAB 2: HISTORY ==================== -->
      <v-tab-item>
        <v-card outlined class="rounded-lg" style="overflow: hidden;">
          <v-card-text>
            <!-- Filters -->
            <v-row dense class="mb-2">
              <v-col cols="12" md="3">
                <v-select
                  v-model="historyFilters.type"
                  :items="[{ text: 'Wszystkie', value: '' }, { text: 'Pojedyncza', value: 'SINGLE' }, { text: 'Masowa', value: 'BULK' }]"
                  :label="$t('notificationLogs.type')"
                  outlined
                  dense
                  @change="loadHistory"
                ></v-select>
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="historyFilters.status"
                  :items="[{ text: 'Wszystkie', value: '' }, { text: 'Wyslano', value: 'SENT' }, { text: 'Blad', value: 'FAILED' }, { text: 'Czesciowo', value: 'PARTIAL' }]"
                  :label="$t('common.status')"
                  outlined
                  dense
                  @change="loadHistory"
                ></v-select>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="historyFilters.search"
                  :label="$t('common.search')"
                  prepend-icon="mdi-magnify"
                  outlined
                  dense
                  clearable
                  @input="loadHistory"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-data-table
              :headers="historyHeaders"
              :items="historyItems"
              :loading="loadingHistory"
              :options.sync="historyOptions"
              :server-items-length="historyTotal"
              class="elevation-0"
              dense
            >
              <template v-slot:top>
                <v-toolbar flat>
                  <v-icon left color="primary">mdi-history</v-icon>
                  <v-toolbar-title>{{ $t('sms.smsHistory') }}</v-toolbar-title>
                </v-toolbar>
              </template>
              <template v-slot:[`item.createdAt`]="{ item }">
                {{ new Date(item.createdAt).toLocaleString('pl-PL') }}
              </template>
              <template v-slot:[`item.type`]="{ item }">
                <v-chip x-small :color="item.type === 'SINGLE' ? 'blue' : 'purple'" text-color="white">
                  {{ item.type === 'SINGLE' ? $t('sms.single') : $t('sms.bulk') }}
                </v-chip>
              </template>
              <template v-slot:[`item.recipients`]="{ item }">
                <span v-if="item.type === 'SINGLE'">{{ getFirstRecipientName(item) }}</span>
                <span v-else>{{ item.totalRecipients }} {{ $t('sms.people') }}</span>
              </template>
              <template v-slot:[`item.content`]="{ item }">
                <span class="sms-content-preserved sms-table-preview">
                  {{ item.content.length > 50 ? item.content.substring(0, 50) + '...' : item.content }}
                </span>
              </template>
              <template v-slot:[`item.status`]="{ item }">
                <v-chip x-small :color="getStatusColor(item.status)" text-color="white">
                  {{ getStatusText(item.status) }}
                </v-chip>
              </template>
              <template v-slot:[`item.replies`]="{ item }">
                <v-badge v-if="item._count.replies > 0" :content="item._count.replies" color="primary" inline />
                <span v-else class="grey--text">0</span>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn x-small color="primary" @click="showMessageDetail(item.id)" class="mr-1">
                  <v-icon x-small>mdi-eye</v-icon>
                </v-btn>
                <v-btn v-if="item.failedCount > 0" x-small color="warning" @click="resendMessage(item.id)">
                  <v-icon x-small>mdi-refresh</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <!-- Message Detail Dialog -->
        <v-dialog content-class="dark-form-dialog" v-model="detailDialog" max-width="700">
          <v-card v-if="detailMessage">
            <v-card-title>
              {{ $t('sms.messageDetails') }}
              <v-spacer></v-spacer>
              <v-chip small :color="getStatusColor(detailMessage.status)" text-color="white">
                {{ getStatusText(detailMessage.status) }}
              </v-chip>
            </v-card-title>
            <v-card-text>
              <div class="mb-3">
                <strong>{{ $t('common.date') }}:</strong> {{ new Date(detailMessage.createdAt).toLocaleString('pl-PL') }}
              </div>
              <div class="mb-3">
                <strong>{{ $t('sms.sender') }}:</strong> {{ detailMessage.sender?.firstName }} {{ detailMessage.sender?.lastName }}
              </div>
              <div class="mb-3">
                <strong>{{ $t('sms.content') }}:</strong>
                <v-card outlined class="pa-3 mt-1 sms-content-preserved">{{ detailMessage.content }}</v-card>
              </div>
              <div class="mb-3">
                <strong>{{ $t('sms.recipientsCount') }} ({{ detailMessage.totalRecipients }}):</strong>
                <v-simple-table dense class="mt-1">
                  <thead>
                    <tr><th>{{ $t('sms.person') }}</th><th>{{ $t('sms.phoneNumber') }}</th><th>{{ $t('common.status') }}</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="(r, idx) in detailMessage.recipients" :key="idx">
                      <td>{{ r.name }}</td>
                      <td>{{ r.phone }}</td>
                      <td>
                        <v-chip x-small :color="r.status === 'SENT' ? 'success' : r.status === 'FAILED' ? 'error' : 'grey'" text-color="white">
                          {{ r.status }}
                        </v-chip>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="detailDialog = false">{{ $t('common.close') }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-tab-item>

      <!-- ==================== TAB 3: REPLIES ==================== -->
      <v-tab-item>
        <v-card outlined class="rounded-lg" style="overflow: hidden;">
          <v-card-text>
            <v-data-table
              :headers="repliesHeaders"
              :items="repliesItems"
              :loading="loadingReplies"
              :options.sync="repliesOptions"
              :server-items-length="repliesTotal"
              class="elevation-0"
              dense
            >
              <template v-slot:top>
                <v-toolbar flat>
                  <v-icon left color="primary">mdi-message-reply</v-icon>
                  <v-toolbar-title>{{ $t('sms.smsReplies') }}</v-toolbar-title>
                </v-toolbar>
              </template>
              <template v-slot:[`item.createdAt`]="{ item }">
                {{ new Date(item.createdAt).toLocaleString('pl-PL') }}
              </template>
              <template v-slot:[`item.read`]="{ item }">
                <v-icon v-if="!item.read" small color="primary">mdi-circle</v-icon>
              </template>
              <template v-slot:[`item.originalContent`]="{ item }">
                <span class="grey--text">{{ item.originalMessage?.content?.substring(0, 30) }}...</span>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn x-small color="primary" @click="replyToSender(item)" class="mr-1">
                  <v-icon x-small left>mdi-reply</v-icon>
                  {{ $t('sms.reply') }}
                </v-btn>
                <v-btn v-if="!item.read" x-small text @click="markAsRead(item.id)">
                  {{ $t('sms.markRead') }}
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- ==================== TAB 4: SCHEDULED ==================== -->
      <v-tab-item>
        <v-card outlined class="rounded-lg" style="overflow: hidden;">
          <v-card-text>
            <v-data-table
              :headers="schedulesHeaders"
              :items="schedules"
              :loading="loadingSchedules"
              class="elevation-0"
              dense
              :items-per-page="20"
            >
              <template v-slot:top>
                <v-toolbar flat>
                  <v-icon left color="primary">mdi-clock-outline</v-icon>
                  <v-toolbar-title>Zaplanowane wysylki</v-toolbar-title>
                  <v-spacer></v-spacer>
                  <span class="grey--text text-caption">Nowa wysylke planujesz z zakladki „Wyslij SMS" (tryb masowy) przyciskiem „Zaplanuj"</span>
                </v-toolbar>
              </template>
              <template v-slot:[`item.name`]="{ item }">
                <div>
                  <strong v-if="item.name">{{ item.name }}</strong>
                  <div class="grey--text sms-content-preserved sms-table-preview" style="font-size: 12px;">
                    {{ item.content.length > 60 ? item.content.substring(0, 60) + '...' : item.content }}
                  </div>
                </div>
              </template>
              <template v-slot:[`item.filter`]="{ item }">
                {{ scheduleFilterLabel(item) }}
              </template>
              <template v-slot:[`item.recurrence`]="{ item }">
                <v-chip x-small :color="item.recurrence === 'ONCE' ? 'blue' : 'purple'" text-color="white">
                  {{ recurrenceLabel(item.recurrence) }}
                </v-chip>
              </template>
              <template v-slot:[`item.nextRunAt`]="{ item }">
                <span v-if="item.active">{{ new Date(item.nextRunAt).toLocaleString('pl-PL') }}</span>
                <span v-else class="grey--text">—</span>
              </template>
              <template v-slot:[`item.lastRunAt`]="{ item }">
                <span v-if="item.lastRunAt">{{ new Date(item.lastRunAt).toLocaleString('pl-PL') }}</span>
                <span v-else class="grey--text">—</span>
              </template>
              <template v-slot:[`item.status`]="{ item }">
                <v-tooltip bottom :disabled="!item.lastError">
                  <template v-slot:activator="{ on }">
                    <v-chip x-small v-on="on" :color="scheduleStatusColor(item)" text-color="white">
                      {{ scheduleStatusText(item) }}
                    </v-chip>
                  </template>
                  <span>{{ item.lastError }}</span>
                </v-tooltip>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                  v-if="item.active"
                  x-small
                  color="warning"
                  class="mr-1"
                  title="Wstrzymaj"
                  @click="toggleSchedule(item, false)"
                >
                  <v-icon x-small>mdi-pause</v-icon>
                </v-btn>
                <v-btn
                  v-else
                  x-small
                  color="success"
                  class="mr-1"
                  title="Wznow"
                  @click="toggleSchedule(item, true)"
                >
                  <v-icon x-small>mdi-play</v-icon>
                </v-btn>
                <v-btn x-small color="primary" class="mr-1" title="Edytuj" @click="openScheduleEdit(item)">
                  <v-icon x-small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn x-small color="error" title="Usun" @click="deleteSchedule(item)">
                  <v-icon x-small>mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <!-- Bulk confirm dialog -->
    <v-dialog content-class="dark-form-dialog" v-model="bulkConfirmDialog" max-width="500">
      <v-card>
        <v-card-title>{{ $t('sms.bulkConfirmTitle') }}</v-card-title>
        <v-card-text>
          <v-alert type="warning" outlined>
            {{ $t('sms.bulkConfirmText', { count: bulkPreview }) }}
          </v-alert>
          <div class="mt-2">
            <strong>{{ $t('sms.content') }}:</strong>
            <div class="sms-dialog-message sms-content-preserved">{{ messageForSend }}</div>
          </div>
          <div class="mt-2">
            <strong>{{ $t('sms.totalSmsCount') }}:</strong> {{ (bulkPreview || 0) * smsCount }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="bulkConfirmDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" :loading="sending" @click="sendBulk">{{ $t('sms.confirmAndSend') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Templates dialog -->
    <v-dialog content-class="dark-form-dialog" v-model="templatesDialog" max-width="640">
      <v-card>
        <v-card-title>
          <v-icon left color="primary">mdi-text-box-multiple-outline</v-icon>
          Szablony SMS
          <v-spacer></v-spacer>
          <v-btn small color="primary" @click="startNewTemplate">
            <v-icon small left>mdi-plus</v-icon>Nowy szablon
          </v-btn>
        </v-card-title>
        <v-card-text>
          <!-- Formularz nowego / edycji -->
          <v-card v-if="templateFormOpen" outlined class="pa-3 mb-3">
            <v-text-field
              v-model="templateForm.name"
              label="Nazwa szablonu"
              outlined
              dense
              counter="120"
            ></v-text-field>
            <v-textarea
              v-model="templateForm.content"
              label="Treść"
              outlined
              dense
              rows="5"
              counter="320"
              maxlength="320"
              :rules="messageRules"
            ></v-textarea>
            <div class="sms-dialog-metrics">
              {{ getSmsMetrics(templateForm.content).characters }}/320 {{ $t('sms.chars') }}
              · {{ getSmsMetrics(templateForm.content).parts }} SMS
              · {{ getSmsMetrics(templateForm.content).encoding }}
            </div>
            <div class="d-flex">
              <v-spacer></v-spacer>
              <v-btn text small @click="templateFormOpen = false">Anuluj</v-btn>
              <v-btn
                color="primary"
                small
                class="ml-2"
                :loading="savingTemplate"
                :disabled="!templateForm.name.trim() || !isValidSmsText(templateForm.content)"
                @click="saveTemplate"
              >Zapisz</v-btn>
            </div>
          </v-card>

          <div v-if="!templates.length && !loadingTemplates" class="grey--text text-center py-4">
            Brak szablonow — dodaj pierwszy przyciskiem powyzej.
          </div>
          <v-list two-line dense style="background: transparent;">
            <v-list-item v-for="t in templates" :key="t.id" style="border-bottom: 1px solid #2A3142;">
              <v-list-item-content>
                <v-list-item-title>{{ t.name }}</v-list-item-title>
                <v-list-item-subtitle class="sms-content-preserved">{{ t.content }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action class="flex-row align-center">
                <v-btn x-small color="primary" class="mr-1" @click="useTemplate(t)">
                  <v-icon x-small left>mdi-arrow-down-bold-box-outline</v-icon>Wstaw
                </v-btn>
                <v-btn x-small text class="mr-1" @click="editTemplate(t)">
                  <v-icon x-small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn x-small text color="error" @click="deleteTemplate(t)">
                  <v-icon x-small>mdi-delete</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="templatesDialog = false">{{ $t('common.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Schedule dialog -->
    <v-dialog content-class="dark-form-dialog" v-model="scheduleDialog" max-width="520">
      <v-card>
        <v-card-title>
          <v-icon left color="primary">mdi-clock-plus-outline</v-icon>
          Zaplanuj wysylke SMS
        </v-card-title>
        <v-card-text>
          <v-alert type="info" outlined dense class="mb-4">
            Odbiorcy: <strong>{{ bulkPreview }} os.</strong>
            <div class="sms-content-preserved mt-2">{{ messageForSend }}</div>
          </v-alert>
          <v-text-field
            v-model="scheduleForm.name"
            label="Nazwa (opcjonalnie, np. Przypomnienie o rozliczeniu)"
            outlined
            dense
          ></v-text-field>
          <v-row dense>
            <v-col cols="6">
              <v-menu
                v-model="scheduleDateMenu"
                :close-on-content-click="false"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="scheduleForm.date"
                    label="Data wysylki"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="scheduleForm.date"
                  :min="todayIso"
                  no-title
                  @input="scheduleDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="scheduleForm.time"
                label="Godzina"
                type="time"
                prepend-inner-icon="mdi-clock-outline"
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>
          <v-select
            v-model="scheduleForm.recurrence"
            :items="recurrenceOptions"
            label="Powtarzanie"
            prepend-inner-icon="mdi-repeat"
            outlined
            dense
          ></v-select>
          <div v-if="scheduleForm.recurrence !== 'ONCE'" class="grey--text" style="font-size: 12px;">
            Kolejne wysylki: {{ recurrenceHint }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="scheduleDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn
            color="primary"
            :loading="schedulingBusy"
            :disabled="!scheduleForm.date || !scheduleForm.time"
            @click="createSchedule"
          >
            <v-icon left>mdi-clock-check-outline</v-icon>Zaplanuj
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Schedule edit dialog -->
    <v-dialog content-class="dark-form-dialog" v-model="scheduleEditDialog" max-width="520">
      <v-card>
        <v-card-title>
          <v-icon left color="primary">mdi-pencil</v-icon>
          Edytuj zaplanowana wysylke
        </v-card-title>
        <v-card-text>
          <v-text-field v-model="scheduleEditForm.name" label="Nazwa (opcjonalnie)" outlined dense></v-text-field>
          <v-textarea
            v-model="scheduleEditForm.content"
            label="Treść"
            outlined
            dense
            rows="5"
            counter="320"
            maxlength="320"
            :rules="messageRules"
          ></v-textarea>
          <v-row dense>
            <v-col cols="6">
              <v-menu v-model="scheduleEditDateMenu" :close-on-content-click="false" offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="scheduleEditForm.date"
                    label="Data najblizszej wysylki"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="scheduleEditForm.date"
                  :min="todayIso"
                  no-title
                  @input="scheduleEditDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="scheduleEditForm.time"
                label="Godzina"
                type="time"
                prepend-inner-icon="mdi-clock-outline"
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>
          <v-select
            v-model="scheduleEditForm.recurrence"
            :items="recurrenceOptions"
            label="Powtarzanie"
            prepend-inner-icon="mdi-repeat"
            outlined
            dense
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="scheduleEditDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn
            color="primary"
            :loading="schedulingBusy"
            :disabled="!isValidSmsText(scheduleEditForm.content) || !scheduleEditForm.date || !scheduleEditForm.time"
            @click="saveScheduleEdit"
          >Zapisz</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Swal from "sweetalert2";
const axios = require("axios");

const GSM_7_BASIC = new Set(Array.from(
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà",
));
const GSM_7_EXTENDED = new Set(Array.from("\f^{}\\[~]|€"));

function normalizeSmsContent(value) {
  return String(value || "")
    .replace(/\r\n?/g, "\n")
    .replace(/\u00a0/g, " ")
    .replace(/[\u200b\ufeff]/g, "")
    .replace(/\t/g, " ")
    .trim();
}

// Zamiana polskich znak\u00f3w na \u0142aci\u0144skie \u2014 wiadomo\u015b\u0107 wraca do alfabetu GSM-7
// (160/153 znak\u00f3w na cz\u0119\u015b\u0107 zamiast 70/67 w Unicode = ta\u0144sza wysy\u0142ka).
const POLISH_MAP = { \u0105: "a", \u0107: "c", \u0119: "e", \u0142: "l", \u0144: "n", \u00f3: "o", \u015b: "s", \u017a: "z", \u017c: "z", \u0104: "A", \u0106: "C", \u0118: "E", \u0141: "L", \u0143: "N", \u00d3: "O", \u015a: "S", \u0179: "Z", \u017b: "Z" };
function stripPolishDiacritics(value) {
  return String(value || "").replace(/[\u0105\u0107\u0119\u0142\u0144\u00f3\u015b\u017a\u017c\u0104\u0106\u0118\u0141\u0143\u00d3\u015a\u0179\u017b]/g, (ch) => POLISH_MAP[ch] || ch);
}

function measureSms(value) {
  const content = normalizeSmsContent(value);
  const characters = Array.from(content);
  let isUnicode = false;
  let units = 0;

  characters.forEach((character) => {
    if (GSM_7_BASIC.has(character)) {
      units += 1;
    } else if (GSM_7_EXTENDED.has(character)) {
      units += 2;
    } else {
      isUnicode = true;
    }
  });

  // SMSAPI używa UCS-2 dla Unicode. Emoji zajmuje w nim dwie jednostki.
  if (isUnicode) units = content.length;

  const singlePartLimit = isUnicode ? 70 : 160;
  const multipartLimit = isUnicode ? 67 : 153;
  const parts = units === 0
    ? 0
    : (units <= singlePartLimit ? 1 : Math.ceil(units / multipartLimit));
  const partLimit = parts <= 1 ? singlePartLimit : multipartLimit;
  const usedInPart = parts === 0 ? 0 : units - ((parts - 1) * partLimit);
  const remaining = parts === 0 ? singlePartLimit : Math.max(0, (parts * partLimit) - units);

  return {
    characters: characters.length,
    encoding: isUnicode ? "Unicode" : "GSM-7",
    isUnicode,
    partLimit,
    parts,
    progress: partLimit ? Math.min(100, (usedInPart / partLimit) * 100) : 0,
    remaining,
    units,
    usedInPart,
  };
}

export default {
  name: "SmsPage",
  data() {
    return {
      activeTab: 0,
      sendType: "SINGLE",
      sending: false,
      searchingRecipients: false,
      recipientSearch: "",
      recipientSearchResults: [],
      searchTimeout: null,
      unreadRepliesCount: 0,

      // Single form
      singleForm: {
        recipient: null,
        phone: "",
      },

      // Bulk form
      bulkForm: {
        filterType: null,
        departmentId: null,
        manualRecipients: [],
        cooperationTypes: [],
        payoutSchedules: [],
        payoutTypes: [],
      },
      bulkPreview: null,
      bulkConfirmDialog: false,
      departments: [],

      bulkFilterOptions: [
        { text: "Wszyscy kierowcy aktywni", value: "ALL_ACTIVE" },
        { text: "Wszyscy kierowcy nieaktywni", value: "ALL_INACTIVE" },
        { text: "Wynajmujacy nasze auta", value: "RENT_DRIVERS" },
        { text: "Kierowcy - konkretny oddzial", value: "DEPARTMENT" },
        { text: "Kierowcy - wg kryteriow", value: "CRITERIA" },
        { text: "Pracownicy / Administratorzy", value: "ADMINS" },
        { text: "Wybierz recznie", value: "MANUAL" },
      ],

      // Kryteria dla filtra CRITERIA (np. miesieczni gotowkowi).
      cooperationTypeOptions: [
        { text: "Najem (RENT)", value: "RENT" },
        { text: "Podlaczenie (CONNECTION)", value: "CONNECTION" },
        { text: "50/50", value: "FIFTY_FIFTY" },
        { text: "Student", value: "STUDENT" },
        { text: "B2B VAT", value: "B2B_VAT" },
        { text: "B2B bez VAT", value: "B2B_NO_VAT" },
      ],
      payoutScheduleOptions: [
        { text: "Miesiecznie", value: "MONTH" },
        { text: "Co 2 tygodnie", value: "EVERY_2_WEEKS" },
        { text: "Tygodniowo", value: "WEEKLY" },
        { text: "Dziennie", value: "DAILY" },
      ],
      payoutTypeOptions: [
        { text: "Gotowka", value: "CASH" },
        { text: "Przelew", value: "WIRE" },
      ],

      // Message form (shared)
      smsForm: {
        message: "",
        allowReply: false,
      },
      // Auto-zamiana polskich znaków (ą→a…) — pamiętana między sesjami.
      stripDiacritics: localStorage.getItem("smsStripPl") === "1",

      // History tab
      loadingHistory: false,
      historyItems: [],
      historyTotal: 0,
      historyOptions: { page: 1, itemsPerPage: 20 },
      historyFilters: { type: "", status: "", search: "" },
      historyHeaders: [
        { text: "Data", value: "createdAt", width: "160px" },
        { text: "Typ", value: "type", width: "100px" },
        { text: "Odbiorca(y)", value: "recipients" },
        { text: "Tresc", value: "content" },
        { text: "Status", value: "status", width: "100px" },
        { text: "Odpowiedzi", value: "replies", width: "80px", sortable: false },
        { text: "Akcje", value: "actions", width: "120px", sortable: false },
      ],

      // Detail dialog
      detailDialog: false,
      detailMessage: null,

      // Templates
      templatesDialog: false,
      templates: [],
      loadingTemplates: false,
      templateFormOpen: false,
      savingTemplate: false,
      templateForm: { id: null, name: "", content: "" },

      // Schedules
      scheduleDialog: false,
      scheduleDateMenu: false,
      schedulingBusy: false,
      scheduleForm: { name: "", date: "", time: "09:00", recurrence: "ONCE" },
      schedules: [],
      loadingSchedules: false,
      scheduleEditDialog: false,
      scheduleEditDateMenu: false,
      scheduleEditForm: { id: null, name: "", content: "", date: "", time: "", recurrence: "ONCE" },
      recurrenceOptions: [
        { text: "Jednorazowo", value: "ONCE" },
        { text: "Codziennie", value: "DAILY" },
        { text: "Co tydzien", value: "WEEKLY" },
        { text: "Co miesiac", value: "MONTHLY" },
      ],
      schedulesHeaders: [
        { text: "Nazwa / Tresc", value: "name" },
        { text: "Odbiorcy", value: "filter", sortable: false },
        { text: "Powtarzanie", value: "recurrence", width: "120px" },
        { text: "Najblizsza wysylka", value: "nextRunAt", width: "160px" },
        { text: "Ostatnia", value: "lastRunAt", width: "160px" },
        { text: "Wyslano", value: "runsCount", width: "80px" },
        { text: "Status", value: "status", width: "110px", sortable: false },
        { text: "Akcje", value: "actions", width: "160px", sortable: false },
      ],

      // Replies tab
      loadingReplies: false,
      repliesItems: [],
      repliesTotal: 0,
      repliesOptions: { page: 1, itemsPerPage: 20 },
      repliesHeaders: [
        { text: "", value: "read", width: "30px", sortable: false },
        { text: "Data", value: "createdAt", width: "160px" },
        { text: "Od", value: "fromName" },
        { text: "Nr telefonu", value: "fromPhone" },
        { text: "Tresc odpowiedzi", value: "content" },
        { text: "W odpowiedzi na", value: "originalContent", sortable: false },
        { text: "Akcje", value: "actions", width: "200px", sortable: false },
      ],
    };
  },
  computed: {
    // Filtr CRITERIA wymaga co najmniej jednego kryterium — bez tego objalby
    // wszystkich aktywnych kierowcow (backend odrzuca taki filtr).
    hasAnyCriterion() {
      return Boolean(
        this.bulkForm.cooperationTypes.length
        || this.bulkForm.payoutSchedules.length
        || this.bulkForm.payoutTypes.length,
      );
    },
    messageRules() {
      return [
        (value) => !!normalizeSmsContent(value) || this.$t("sms.contentRequired"),
        (value) => String(value || "").length <= 320 || this.$t("sms.maxChars"),
      ];
    },
    messageForSend() {
      return normalizeSmsContent(this.smsForm.message);
    },
    smsMetrics() {
      return measureSms(this.smsForm.message);
    },
    charCount() {
      return this.smsMetrics.characters;
    },
    smsCount() {
      return this.smsMetrics.parts;
    },
    recipientCount() {
      if (this.sendType === "SINGLE") {
        return String(this.singleForm.phone || "").trim() ? 1 : 0;
      }
      return this.bulkPreview || 0;
    },
    canSendSingle() {
      return Boolean(
        String(this.singleForm.phone || "").trim()
        && this.isValidSmsText(this.smsForm.message),
      );
    },
    canSendBulk() {
      return Boolean(
        this.bulkForm.filterType
        && this.isValidSmsText(this.smsForm.message)
        && this.bulkPreview > 0,
      );
    },
    todayIso() {
      return new Date().toISOString().substring(0, 10);
    },
    recurrenceHint() {
      const map = {
        DAILY: "codziennie o tej samej godzinie",
        WEEKLY: "co tydzien, w ten sam dzien tygodnia",
        MONTHLY: "co miesiac, tego samego dnia miesiaca",
      };
      return map[this.scheduleForm.recurrence] || "";
    },
  },
  watch: {
    // Przełącznik „usuń polskie znaki": zamiana od razu i przy dalszym pisaniu
    // (WYSIWYG — licznik i treść w polu zawsze pokazują to, co realnie wyjdzie).
    stripDiacritics(v) {
      localStorage.setItem("smsStripPl", v ? "1" : "0");
      if (v) this.smsForm.message = stripPolishDiacritics(this.smsForm.message);
    },
    "smsForm.message"(v) {
      if (!this.stripDiacritics) return;
      const s = stripPolishDiacritics(v);
      if (s !== v) this.smsForm.message = s;
    },
    recipientSearch(val) {
      if (!val || val.length < 2) {
        this.recipientSearchResults = [];
        return;
      }
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => this.searchRecipients(val), 300);
    },
    "historyOptions.page"() {
      this.loadHistory();
    },
    "repliesOptions.page"() {
      this.loadReplies();
    },
    "bulkForm.manualRecipients": {
      deep: true,
      handler(recipients) {
        if (this.bulkForm.filterType === "MANUAL") {
          this.bulkPreview = recipients.length;
        }
      },
    },
    activeTab(val) {
      if (val === 1) this.loadHistory();
      if (val === 2) this.loadReplies();
      if (val === 3) this.loadSchedules();
    },
  },
  async created() {
    await this.loadDepartments();
    this.loadUnreadCount();
  },
  methods: {
    normalizeSmsContent,
    getSmsMetrics(value) {
      return measureSms(value);
    },
    isValidSmsText(value) {
      const content = normalizeSmsContent(value);
      return Boolean(content && String(value || "").length <= 320);
    },
    setComposerMessage(value, cursorPosition) {
      if (String(value || "").length > 320) {
        Swal.fire({
          title: this.$t("sms.limitReached"),
          text: this.$t("sms.maxChars"),
          icon: "warning",
          toast: true,
          position: "top-end",
          timer: 2600,
          showConfirmButton: false,
        });
        return;
      }

      this.smsForm.message = value;
      this.$nextTick(() => {
        const field = this.$refs.smsMessageInput;
        const textarea = field && field.$refs ? field.$refs.input : null;
        if (!textarea) return;
        textarea.focus();
        if (typeof cursorPosition === "number") {
          textarea.setSelectionRange(cursorPosition, cursorPosition);
        }
      });
    },
    addGreeting() {
      const current = this.smsForm.message || "";
      if (/^\s*Dzień dobry,/i.test(current)) {
        this.setComposerMessage(current, current.length);
        return;
      }
      const greeting = "Dzień dobry,";
      const value = current ? `${greeting}\n\n${current.replace(/^\s+/, "")}` : `${greeting}\n\n`;
      this.setComposerMessage(value, greeting.length + 2);
    },
    addParagraph() {
      const current = this.smsForm.message || "";
      const field = this.$refs.smsMessageInput;
      const textarea = field && field.$refs ? field.$refs.input : null;
      const start = textarea && typeof textarea.selectionStart === "number"
        ? textarea.selectionStart
        : current.length;
      const end = textarea && typeof textarea.selectionEnd === "number"
        ? textarea.selectionEnd
        : start;
      const value = `${current.substring(0, start)}\n\n${current.substring(end)}`;
      this.setComposerMessage(value, start + 2);
    },
    addSignature() {
      const signature = "Pozdrawiamy,\nZespół BTP";
      const current = this.smsForm.message || "";
      if (current.includes(signature)) {
        this.setComposerMessage(current, current.length);
        return;
      }
      const body = current.replace(/\s+$/, "");
      const value = body ? `${body}\n\n${signature}` : signature;
      this.setComposerMessage(value, value.length);
    },
    async confirmClearForm() {
      const hasDraft = Boolean(
        normalizeSmsContent(this.smsForm.message)
        || this.singleForm.phone
        || this.bulkForm.filterType,
      );
      if (!hasDraft) {
        this.clearForm();
        return;
      }

      const result = await Swal.fire({
        title: this.$t("sms.clearDraftTitle"),
        text: this.$t("sms.clearDraftText"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("sms.clear"),
        cancelButtonText: this.$t("common.cancel"),
      });
      if (result.isConfirmed) this.clearForm();
    },

    // ===== SEARCH =====
    async searchRecipients(search) {
      this.searchingRecipients = true;
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `sms/recipients?search=${encodeURIComponent(search)}`,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.recipientSearchResults = res.data.data;
      } catch (e) {
        console.log(e);
      } finally {
        this.searchingRecipients = false;
      }
    },

    onRecipientSelected(recipient) {
      if (recipient) {
        this.singleForm.phone = recipient.phone;
      }
    },

    // ===== SEND SINGLE =====
    async sendSingle() {
      if (!this.canSendSingle) return;
      this.sending = true;
      try {
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "sms/send",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          data: {
            driverId: this.singleForm.recipient?.type === "driver" ? this.singleForm.recipient.id : undefined,
            adminId: this.singleForm.recipient?.type === "admin" ? this.singleForm.recipient.id : undefined,
            phone: this.singleForm.phone,
            recipientName: this.singleForm.recipient?.name || this.singleForm.phone,
            message: this.messageForSend,
            allowReply: this.smsForm.allowReply,
          },
        });
        Swal.fire({
          title: "SMS wyslany!",
          text: `Wyslano do ${this.singleForm.recipient?.name || this.singleForm.phone}`,
          icon: "success",
          timer: 3000,
        });
        this.clearForm();
      } catch (e) {
        console.log(e);
        Swal.fire("Blad", e.response?.data?.message || "Nie udalo sie wyslac SMS", "error");
      } finally {
        this.sending = false;
      }
    },

    // ===== SEND BULK =====
    confirmBulkSend() {
      this.bulkConfirmDialog = true;
    },

    async sendBulk() {
      this.sending = true;
      try {
        const filter = this.buildFilter();
        const res = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "sms/send-bulk",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          data: {
            filter,
            message: this.messageForSend,
            allowReply: this.smsForm.allowReply,
          },
        });
        this.bulkConfirmDialog = false;
        const data = res.data.data;
        Swal.fire({
          title: "Wysylka zakonczona!",
          text: `Wyslano: ${data.sentCount}/${data.totalRecipients}. Bledy: ${data.failedCount}.`,
          icon: data.failedCount === 0 ? "success" : "warning",
        });
        this.clearForm();
      } catch (e) {
        console.log(e);
        Swal.fire("Blad", e.response?.data?.message || "Nie udalo sie wyslac SMS", "error");
      } finally {
        this.sending = false;
      }
    },

    // ===== BULK PREVIEW =====
    async previewBulkRecipients() {
      if (!this.bulkForm.filterType) {
        this.bulkPreview = null;
        return;
      }
      if (this.bulkForm.filterType === "MANUAL") {
        this.bulkPreview = this.bulkForm.manualRecipients.length;
        return;
      }
      // Dokładny licznik rozwiązywany po stronie serwera (ten sam filtr co wysyłka).
      if (this.bulkForm.filterType === "CRITERIA" && !this.hasAnyCriterion) {
        // Backend odrzuci filtr bez kryteriow — nie ma sensu go pytac.
        this.bulkPreview = 0;
        return;
      }
      try {
        const filter = this.buildFilter();
        const res = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "sms/preview-bulk",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          data: { filter },
        });
        this.bulkPreview = res.data?.data?.count ?? 0;
      } catch (e) {
        this.bulkPreview = 0;
      }
    },

    // ===== HISTORY =====
    async loadHistory() {
      this.loadingHistory = true;
      try {
        const params = new URLSearchParams({
          take: String(this.historyOptions.itemsPerPage),
          skip: String(this.historyOptions.page),
        });
        if (this.historyFilters.type) params.set("type", this.historyFilters.type);
        if (this.historyFilters.status) params.set("status", this.historyFilters.status);
        if (this.historyFilters.search) params.set("search", this.historyFilters.search);

        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "sms/history?" + params.toString(),
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.historyItems = res.data.data.result;
        this.historyTotal = res.data.data.count;
      } catch (e) {
        console.log(e);
      } finally {
        this.loadingHistory = false;
      }
    },

    // ===== MESSAGE DETAIL =====
    async showMessageDetail(id) {
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "sms/message/" + id,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.detailMessage = res.data.data;
        this.detailDialog = true;
      } catch (e) {
        console.log(e);
        Swal.fire("Blad", "Nie udalo sie pobrac szczegolow", "error");
      }
    },

    // ===== RESEND =====
    async resendMessage(id) {
      try {
        const res = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "sms/resend/" + id,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        Swal.fire("Ponowiono!", `Wyslano: ${res.data.data.sentCount}, Bledy: ${res.data.data.failedCount}`, "success");
        await this.loadHistory();
      } catch (e) {
        console.log(e);
        Swal.fire("Blad", e.response?.data?.message || "Wystapil blad", "error");
      }
    },

    // ===== REPLIES =====
    async loadReplies() {
      this.loadingReplies = true;
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `sms/replies?take=${this.repliesOptions.itemsPerPage}&skip=${this.repliesOptions.page}`,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.repliesItems = res.data.data.result;
        this.repliesTotal = res.data.data.count;
        this.unreadRepliesCount = res.data.data.unreadCount;
      } catch (e) {
        console.log(e);
      } finally {
        this.loadingReplies = false;
      }
    },

    async markAsRead(id) {
      try {
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "sms/replies/" + id + "/read",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        await this.loadReplies();
      } catch (e) {
        console.log(e);
      }
    },

    replyToSender(reply) {
      this.activeTab = 0;
      this.sendType = "SINGLE";
      this.singleForm.phone = reply.fromPhone;
      this.singleForm.recipient = {
        name: reply.fromName || reply.fromPhone,
        phone: reply.fromPhone,
      };
    },

    async loadUnreadCount() {
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "sms/replies/unread-count",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.unreadRepliesCount = res.data.data;
      } catch (e) {
        // silent
      }
    },

    // ===== DEPARTMENTS =====
    async loadDepartments() {
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "sub-company",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.departments = (res.data.data || []).map((d) => ({ id: d.id, name: d.name }));
      } catch (e) {
        console.log(e);
      }
    },

    // ===== TEMPLATES =====
    async openTemplatesDialog() {
      this.templatesDialog = true;
      this.templateFormOpen = false;
      await this.loadTemplates();
    },

    async loadTemplates() {
      this.loadingTemplates = true;
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "sms/templates",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.templates = res.data.data;
      } catch (e) {
        console.log(e);
      } finally {
        this.loadingTemplates = false;
      }
    },

    startNewTemplate() {
      // nowy szablon — podpowiedz tresc z composera, jesli cos wpisano
      this.templateForm = { id: null, name: "", content: this.smsForm.message || "" };
      this.templateFormOpen = true;
    },

    editTemplate(t) {
      this.templateForm = { id: t.id, name: t.name, content: t.content };
      this.templateFormOpen = true;
    },

    async saveTemplate() {
      if (!this.templateForm.name.trim() || !this.isValidSmsText(this.templateForm.content)) return;
      const content = normalizeSmsContent(this.templateForm.content);
      this.savingTemplate = true;
      try {
        if (this.templateForm.id) {
          await axios({
            method: "patch",
            url: process.env.VUE_APP_ROOT_API + "sms/templates/" + this.templateForm.id,
            headers: { Authorization: "Bearer " + localStorage.getItem("token") },
            data: { name: this.templateForm.name.trim(), content },
          });
        } else {
          await axios({
            method: "post",
            url: process.env.VUE_APP_ROOT_API + "sms/templates",
            headers: { Authorization: "Bearer " + localStorage.getItem("token") },
            data: { name: this.templateForm.name.trim(), content },
          });
        }
        this.templateFormOpen = false;
        await this.loadTemplates();
      } catch (e) {
        Swal.fire("Blad", e.response?.data?.message || "Nie udalo sie zapisac szablonu", "error");
      } finally {
        this.savingTemplate = false;
      }
    },

    useTemplate(t) {
      this.smsForm.message = t.content;
      this.templatesDialog = false;
    },

    async deleteTemplate(t) {
      const c = await Swal.fire({
        title: "Usunac szablon?",
        text: t.name,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Usun",
        cancelButtonText: "Anuluj",
      });
      if (!c.isConfirmed) return;
      try {
        await axios({
          method: "delete",
          url: process.env.VUE_APP_ROOT_API + "sms/templates/" + t.id,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        await this.loadTemplates();
      } catch (e) {
        Swal.fire("Blad", e.response?.data?.message || "Nie udalo sie usunac", "error");
      }
    },

    // ===== SCHEDULES =====
    openScheduleDialog() {
      const tomorrow = new Date(Date.now() + 24 * 3600 * 1000);
      this.scheduleForm = {
        name: "",
        date: tomorrow.toISOString().substring(0, 10),
        time: "09:00",
        recurrence: "ONCE",
      };
      this.scheduleDialog = true;
    },

    buildFilter() {
      const isCriteria = this.bulkForm.filterType === "CRITERIA";
      return {
        filterType: this.bulkForm.filterType,
        departmentId: this.bulkForm.departmentId || undefined,
        manualIds: this.bulkForm.filterType === "MANUAL"
          ? this.bulkForm.manualRecipients.map((r) => r.id)
          : undefined,
        // Kryteria wysylamy tylko dla CRITERIA — puste tablice pomijamy,
        // zeby backend nie dostal warunku zawezajacego do zera rekordow.
        cooperationTypes: isCriteria && this.bulkForm.cooperationTypes.length
          ? this.bulkForm.cooperationTypes
          : undefined,
        payoutSchedules: isCriteria && this.bulkForm.payoutSchedules.length
          ? this.bulkForm.payoutSchedules
          : undefined,
        payoutTypes: isCriteria && this.bulkForm.payoutTypes.length
          ? this.bulkForm.payoutTypes
          : undefined,
      };
    },

    async createSchedule() {
      const sendAt = new Date(this.scheduleForm.date + "T" + this.scheduleForm.time + ":00");
      if (isNaN(sendAt.getTime())) {
        Swal.fire("Blad", "Nieprawidlowa data lub godzina", "error");
        return;
      }
      if (sendAt.getTime() < Date.now()) {
        Swal.fire("Blad", "Termin wysylki musi byc w przyszlosci", "error");
        return;
      }
      this.schedulingBusy = true;
      try {
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "sms/schedules",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          data: {
            name: this.scheduleForm.name || undefined,
            content: this.messageForSend,
            allowReply: this.smsForm.allowReply,
            filter: this.buildFilter(),
            sendAt: sendAt.toISOString(),
            recurrence: this.scheduleForm.recurrence,
          },
        });
        this.scheduleDialog = false;
        Swal.fire({
          title: "Zaplanowano!",
          text: `Wysylka ${sendAt.toLocaleString("pl-PL")} do ${this.bulkPreview} os.`,
          icon: "success",
          timer: 4000,
        });
        this.clearForm();
        this.activeTab = 3;
      } catch (e) {
        Swal.fire("Blad", e.response?.data?.message || "Nie udalo sie zaplanowac", "error");
      } finally {
        this.schedulingBusy = false;
      }
    },

    async loadSchedules() {
      this.loadingSchedules = true;
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "sms/schedules",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.schedules = res.data.data;
      } catch (e) {
        console.log(e);
      } finally {
        this.loadingSchedules = false;
      }
    },

    async toggleSchedule(item, active) {
      try {
        await axios({
          method: "patch",
          url: process.env.VUE_APP_ROOT_API + "sms/schedules/" + item.id,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          data: { active },
        });
        await this.loadSchedules();
      } catch (e) {
        Swal.fire("Blad", e.response?.data?.message || "Nie udalo sie zmienic statusu", "error");
      }
    },

    openScheduleEdit(item) {
      const d = new Date(item.nextRunAt);
      this.scheduleEditForm = {
        id: item.id,
        name: item.name || "",
        content: item.content,
        date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
        time: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
        recurrence: item.recurrence,
      };
      this.scheduleEditDialog = true;
    },

    async saveScheduleEdit() {
      const f = this.scheduleEditForm;
      if (!this.isValidSmsText(f.content)) return;
      const sendAt = new Date(f.date + "T" + f.time + ":00");
      this.schedulingBusy = true;
      try {
        await axios({
          method: "patch",
          url: process.env.VUE_APP_ROOT_API + "sms/schedules/" + f.id,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          data: {
            name: f.name || null,
            content: normalizeSmsContent(f.content),
            sendAt: sendAt.toISOString(),
            recurrence: f.recurrence,
          },
        });
        this.scheduleEditDialog = false;
        await this.loadSchedules();
      } catch (e) {
        Swal.fire("Blad", e.response?.data?.message || "Nie udalo sie zapisac", "error");
      } finally {
        this.schedulingBusy = false;
      }
    },

    async deleteSchedule(item) {
      const c = await Swal.fire({
        title: "Usunac zaplanowana wysylke?",
        text: item.name || item.content.substring(0, 60),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Usun",
        cancelButtonText: "Anuluj",
      });
      if (!c.isConfirmed) return;
      try {
        await axios({
          method: "delete",
          url: process.env.VUE_APP_ROOT_API + "sms/schedules/" + item.id,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        await this.loadSchedules();
      } catch (e) {
        Swal.fire("Blad", e.response?.data?.message || "Nie udalo sie usunac", "error");
      }
    },

    scheduleFilterLabel(item) {
      const f = item.filter || {};
      const map = {
        ALL_ACTIVE: "Wszyscy aktywni",
        ALL_INACTIVE: "Wszyscy nieaktywni",
        RENT_DRIVERS: "Wynajmujacy auta",
        ADMINS: "Pracownicy/Admini",
        DEPARTMENT: "Oddzial",
        CRITERIA: "Wg kryteriow",
        MANUAL: "Wybrani recznie",
      };
      let label = map[f.filterType] || f.filterType || "—";
      if (f.filterType === "DEPARTMENT" && f.departmentId) {
        const d = this.departments.find((x) => x.id === f.departmentId);
        if (d) label += ": " + d.name;
      }
      if (f.filterType === "CRITERIA") {
        const named = (opts, vals) => (vals || [])
          .map((v) => (opts.find((o) => o.value === v) || {}).text || v);
        const parts = [
          ...named(this.payoutScheduleOptions, f.payoutSchedules),
          ...named(this.payoutTypeOptions, f.payoutTypes),
          ...named(this.cooperationTypeOptions, f.cooperationTypes),
        ];
        if (parts.length) label += ": " + parts.join(" + ");
      }
      if (f.filterType === "MANUAL") {
        label += ` (${(f.manualIds || []).length} os.)`;
      }
      return label;
    },

    recurrenceLabel(r) {
      return { ONCE: "Jednorazowo", DAILY: "Codziennie", WEEKLY: "Co tydzien", MONTHLY: "Co miesiac" }[r] || r;
    },

    scheduleStatusColor(item) {
      if (item.lastError) return "error";
      if (!item.active) return item.runsCount > 0 && item.recurrence === "ONCE" ? "blue" : "grey";
      return "success";
    },

    scheduleStatusText(item) {
      if (item.lastError) return "Blad";
      if (!item.active) return item.runsCount > 0 && item.recurrence === "ONCE" ? "Wyslano" : "Wstrzymana";
      return "Aktywna";
    },

    // ===== HELPERS =====
    getFirstRecipientName(item) {
      const recipients = item.recipients;
      if (Array.isArray(recipients) && recipients.length > 0) {
        return recipients[0].name;
      }
      return "-";
    },

    getStatusColor(status) {
      return { SENT: "success", FAILED: "error", PARTIAL: "warning", PENDING: "grey" }[status] || "grey";
    },

    getStatusText(status) {
      return { SENT: "Wyslano", FAILED: "Blad", PARTIAL: "Czesciowo", PENDING: "Oczekujacy" }[status] || status;
    },

    clearForm() {
      this.singleForm = { recipient: null, phone: "" };
      this.bulkForm = {
        filterType: null,
        departmentId: null,
        manualRecipients: [],
        cooperationTypes: [],
        payoutSchedules: [],
        payoutTypes: [],
      };
      this.smsForm = { message: "", allowReply: false };
      this.bulkPreview = null;
      this.recipientSearch = "";
      this.recipientSearchResults = [];
    },
  },
};
</script>

<style scoped>
.sms-page {
  margin: -24px;
  padding: 24px;
  background: #181C28;
  min-height: calc(100vh - 64px);
  color: #E6EAF2;
}
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 20px;
}
.page-eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #5478FF;
  font-weight: 600;
}
.page-title {
  font-size: 28px;
  font-weight: 700;
  margin: 4px 0 0 0;
  color: #FFFFFF;
}
.sms-tabs {
  background: #232838 !important;
  border-radius: 12px 12px 0 0;
  border: 1px solid #2A3142;
  padding: 0 8px;
}
.sms-tabs >>> .v-tabs-bar {
  background: transparent !important;
}
.sms-tabs >>> .v-tab {
  color: #94A0B8 !important;
  font-weight: 600;
  letter-spacing: -0.01em;
  text-transform: none;
}
.sms-tabs >>> .v-tab--active {
  color: #5478FF !important;
}
.sms-tabs >>> .v-tabs-slider {
  background-color: #5478FF !important;
}
.sms-page >>> .v-tabs-items,
.sms-page >>> .v-window {
  background: transparent !important;
}
.sms-page >>> .v-card {
  background: #232838 !important;
  color: #E6EAF2 !important;
  border-color: #2A3142 !important;
}
.sms-page >>> .v-card__title {
  color: #FFFFFF !important;
}
.sms-page >>> .v-text-field--outlined fieldset {
  border-color: #2A3142 !important;
}
.sms-page >>> .v-text-field input,
.sms-page >>> .v-text-field textarea,
.sms-page >>> .v-select__selection {
  color: #E6EAF2 !important;
}
.sms-page >>> .v-label {
  color: #94A0B8 !important;
}
.sms-page >>> .v-data-table {
  background: transparent !important;
  color: #E6EAF2 !important;
}
.sms-page >>> .v-data-table thead tr th {
  background: #1F2433 !important;
  color: #94A0B8 !important;
  border-bottom: 1px solid #2A3142 !important;
}
.sms-page >>> .v-data-table tbody tr td {
  border-bottom: 1px solid #2A3142 !important;
  color: #E6EAF2 !important;
}

.sms-composer-card {
  overflow: hidden;
  border-radius: 18px !important;
  box-shadow: 0 20px 55px rgba(7, 10, 20, 0.24) !important;
}
.sms-composer-hero {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 128px;
  padding: 26px 30px;
  overflow: hidden;
  border-bottom: 1px solid rgba(120, 160, 255, 0.18);
  background:
    radial-gradient(circle at 82% 15%, rgba(104, 87, 255, 0.25), transparent 34%),
    linear-gradient(125deg, #252f54 0%, #263860 46%, #23304d 100%);
}
.sms-composer-hero::after {
  position: absolute;
  right: -58px;
  bottom: -92px;
  width: 260px;
  height: 260px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 50%;
  content: "";
}
.sms-composer-hero__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 52px;
  height: 52px;
  margin-right: 18px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 15px;
  background: rgba(84, 120, 255, 0.4);
  box-shadow: 0 12px 30px rgba(12, 20, 50, 0.24);
}
.sms-composer-eyebrow {
  margin-bottom: 3px;
  color: #9ab5ff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}
.sms-composer-hero h2 {
  margin: 0;
  color: #fff;
  font-size: 25px;
  font-weight: 750;
  letter-spacing: -0.02em;
}
.sms-composer-hero p {
  max-width: 680px;
  margin: 5px 0 0;
  color: #c4cee4;
  font-size: 14px;
}
.sms-live-pill {
  z-index: 1;
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  margin-left: auto;
  padding: 8px 12px;
  border: 1px solid rgba(141, 231, 187, 0.22);
  border-radius: 999px;
  background: rgba(16, 29, 45, 0.42);
  color: #bcf2d7;
  font-size: 12px;
  font-weight: 600;
}
.sms-live-pill__dot {
  width: 7px;
  height: 7px;
  margin-right: 7px;
  border-radius: 50%;
  background: #54d99a;
  box-shadow: 0 0 0 4px rgba(84, 217, 154, 0.12);
}
.sms-composer-body {
  padding: 22px !important;
}
.sms-section-card {
  height: 100%;
  padding: 22px;
  border: 1px solid #30384d;
  border-radius: 16px;
  background: #1e2432;
}
.sms-section-heading {
  display: flex;
  align-items: flex-start;
  margin-bottom: 20px;
}
.sms-section-step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  margin-right: 11px;
  border: 1px solid rgba(120, 160, 255, 0.35);
  border-radius: 9px;
  background: rgba(84, 120, 255, 0.14);
  color: #8faaff;
  font-size: 12px;
  font-weight: 800;
}
.sms-section-heading h3 {
  margin: 1px 0 2px;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
}
.sms-section-heading p {
  margin: 0;
  color: #8f9ab1;
  font-size: 12px;
  line-height: 1.45;
}
.sms-mode-toggle {
  display: flex !important;
  width: 100%;
  margin-bottom: 22px;
  padding: 4px;
  border: 1px solid #30384d;
  border-radius: 11px;
  background: #191e2a !important;
  box-shadow: none !important;
}
.sms-mode-toggle >>> .v-btn {
  flex: 1 1 50%;
  min-width: 0 !important;
  height: 38px !important;
  border: 0 !important;
  border-radius: 8px !important;
  background: transparent !important;
  color: #8f9ab1 !important;
  font-size: 12px;
  letter-spacing: 0;
  text-transform: none;
}
.sms-mode-toggle >>> .v-btn--active {
  background: linear-gradient(135deg, #4f71df, #6658e8) !important;
  color: #fff !important;
  box-shadow: 0 6px 16px rgba(52, 78, 169, 0.32) !important;
}
.sms-recipient-card >>> .v-input {
  margin-bottom: 2px;
}
.sms-recipient-card >>> .v-input__prepend-inner {
  margin-right: 5px;
}
.sms-recipient-summary {
  display: flex;
  align-items: center;
  min-height: 68px;
  margin-top: 6px;
  padding: 13px 15px;
  border: 1px solid rgba(89, 211, 155, 0.22);
  border-radius: 12px;
  background: rgba(39, 149, 106, 0.09);
}
.sms-recipient-summary--empty {
  border-color: #30384d;
  background: rgba(255, 255, 255, 0.02);
}
.sms-recipient-summary__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  margin-right: 11px;
  border-radius: 10px;
  background: rgba(89, 211, 155, 0.14);
  color: #6bdda8;
}
.sms-recipient-summary--empty .sms-recipient-summary__icon {
  background: rgba(143, 154, 177, 0.1);
  color: #8f9ab1;
}
.sms-recipient-summary span {
  display: block;
  color: #8f9ab1;
  font-size: 11px;
}
.sms-recipient-summary strong {
  display: block;
  margin-top: -1px;
  color: #fff;
  font-size: 20px;
  line-height: 1.2;
}
.sms-editor-row {
  margin-top: -8px;
}
.sms-quick-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 7px;
  margin-bottom: 10px;
}
.sms-quick-actions > span {
  margin-right: 2px;
  color: #7f8ba4;
  font-size: 11px;
  font-weight: 650;
}
.sms-quick-actions >>> .v-btn {
  border-color: #3a4661 !important;
  border-radius: 8px;
  color: #bdc9df !important;
  letter-spacing: 0;
  text-transform: none;
}
.sms-quick-actions >>> .v-btn:hover {
  border-color: #617ed4 !important;
  background: rgba(84, 120, 255, 0.09) !important;
  color: #fff !important;
}
.sms-message-input {
  margin-top: 0 !important;
}
.sms-message-input >>> textarea {
  padding-top: 14px !important;
  color: #f1f4fb !important;
  font-size: 14px;
  line-height: 1.62;
}
.sms-message-input >>> fieldset {
  border-color: #39435a !important;
  border-radius: 12px;
}
.sms-message-input >>> .v-input--is-focused fieldset,
.sms-message-input.v-input--is-focused >>> fieldset {
  border-color: #6f8cff !important;
}
.sms-message-input >>> .v-counter {
  color: #77839a;
  font-size: 11px;
}
.sms-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-top: -7px;
}
.sms-metric {
  min-width: 0;
  padding: 10px 11px;
  border: 1px solid #30384d;
  border-radius: 10px;
  background: #1a202c;
}
.sms-metric span {
  display: block;
  overflow: hidden;
  color: #7f8ba4;
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sms-metric strong {
  display: block;
  margin-top: 2px;
  overflow: hidden;
  color: #f1f4fb;
  font-size: 15px;
  line-height: 1.2;
  text-overflow: ellipsis;
}
.sms-metric strong small {
  margin-left: 2px;
  color: #6f7b92;
  font-size: 10px;
  font-weight: 500;
}
.sms-segment-progress {
  margin-top: 13px;
}
.sms-segment-progress > div:first-child {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  color: #95a0b6;
  font-size: 10px;
}
.sms-segment-progress small {
  display: block;
  margin-top: 5px;
  color: #707b91;
  font-size: 10px;
  text-align: right;
}
.sms-unicode-alert {
  margin: 12px 0 0 !important;
  font-size: 11px;
  line-height: 1.45;
}
.sms-reply-checkbox {
  margin-top: 10px;
  padding-top: 0;
}
.sms-reply-checkbox >>> .v-label {
  font-size: 12px;
}
.sms-phone-preview {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 275px;
  min-height: 418px;
  margin: 0 auto;
  padding: 10px 9px 12px;
  overflow: hidden;
  border: 1px solid #3b465e;
  border-radius: 28px;
  background: #0e121b;
  box-shadow:
    0 20px 42px rgba(5, 8, 16, 0.42),
    inset 0 0 0 2px rgba(255, 255, 255, 0.025);
}
.sms-phone-preview__top {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: center;
  height: 25px;
  padding: 0 9px;
  color: #dfe6f6;
  font-size: 9px;
}
.sms-phone-preview__top > :last-child {
  justify-self: end;
}
.sms-phone-preview__speaker {
  width: 45px;
  height: 5px;
  margin: 0 auto;
  border-radius: 99px;
  background: #242b38;
}
.sms-phone-preview__contact {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 9px 8px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.055);
}
.sms-phone-preview__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  margin-bottom: 5px;
  border-radius: 50%;
  background: linear-gradient(145deg, #5478ff, #7358e8);
  color: #fff;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.05em;
}
.sms-phone-preview__contact strong {
  color: #f2f5fb;
  font-size: 11px;
}
.sms-phone-preview__contact span {
  color: #657086;
  font-size: 8px;
}
.sms-phone-preview__screen {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: flex-start;
  padding: 21px 8px 12px;
  background:
    radial-gradient(circle at 80% 8%, rgba(84, 120, 255, 0.08), transparent 35%),
    #121722;
}
.sms-phone-preview__bubble {
  max-width: 90%;
  padding: 10px 12px;
  border-radius: 15px 15px 15px 4px;
  background: #293245;
  color: #f2f4f9;
  font-size: 10px;
  line-height: 1.48;
  box-shadow: 0 6px 14px rgba(5, 8, 15, 0.16);
}
.sms-phone-preview__bubble--empty {
  color: #718097;
  font-style: italic;
}
.sms-phone-preview__time {
  margin: 4px 0 0 5px;
  color: #596579;
  font-size: 8px;
}
.sms-phone-preview__home {
  width: 82px;
  height: 3px;
  margin: 8px auto 0;
  border-radius: 99px;
  background: #3a4250;
}
.sms-actions {
  display: flex;
  align-items: center;
  gap: 18px;
  margin-top: 18px;
  padding: 17px 18px;
  border: 1px solid #30384d;
  border-radius: 14px;
  background: #1d2331;
}
.sms-actions__summary {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 200px;
  color: #8e9ab1;
  font-size: 11px;
  line-height: 1.45;
}
.sms-actions__buttons {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: auto;
}
.sms-actions__buttons >>> .v-btn {
  margin: 0 !important;
  border-radius: 10px;
  letter-spacing: 0;
  text-transform: none;
}
.sms-primary-action {
  min-width: 138px !important;
  background: linear-gradient(135deg, #4f73e8, #6659e8) !important;
  box-shadow: 0 9px 22px rgba(69, 85, 210, 0.3) !important;
}
.sms-content-preserved {
  white-space: pre-wrap !important;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.sms-table-preview {
  display: -webkit-box;
  overflow: hidden;
  white-space: pre-line !important;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
.sms-dialog-message {
  max-height: 220px;
  margin-top: 7px;
  padding: 12px 14px;
  overflow-y: auto;
  border: 1px solid #343d51;
  border-radius: 10px;
  background: #1b202c;
  color: #e9edf6;
  line-height: 1.55;
}
.sms-dialog-metrics {
  margin: -14px 0 12px;
  color: #8490a7;
  font-size: 11px;
  text-align: right;
}
.sms-page >>> .sms-content-preserved.v-list-item__subtitle {
  display: -webkit-box !important;
  max-height: 42px;
  line-height: 1.4;
  white-space: pre-line !important;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

@media (max-width: 1263px) {
  .sms-recipient-card {
    height: auto;
  }
  .sms-phone-preview {
    max-width: 300px;
  }
}

@media (max-width: 959px) {
  .sms-composer-hero {
    padding: 22px;
  }
  .sms-live-pill {
    display: none;
  }
  .sms-actions {
    align-items: flex-start;
    flex-direction: column;
  }
  .sms-actions__buttons {
    justify-content: flex-start;
    width: 100%;
    margin-left: 0;
  }
}

@media (max-width: 599px) {
  .sms-page {
    margin: -16px;
    padding: 16px;
  }
  .sms-composer-hero {
    align-items: flex-start;
    padding: 20px 18px;
  }
  .sms-composer-hero__icon {
    width: 42px;
    height: 42px;
    margin-right: 12px;
  }
  .sms-composer-hero h2 {
    font-size: 20px;
  }
  .sms-composer-hero p {
    font-size: 12px;
  }
  .sms-composer-body {
    padding: 12px !important;
  }
  .sms-section-card {
    padding: 16px;
  }
  .sms-mode-toggle >>> .v-btn {
    padding: 0 8px !important;
    font-size: 11px;
  }
  .sms-metrics {
    grid-template-columns: 1fr;
  }
  .sms-metric {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .sms-metric strong {
    margin-top: 0;
  }
  .sms-phone-preview {
    min-height: 390px;
  }
  .sms-actions__buttons >>> .v-btn {
    flex: 1 1 auto;
  }
}
</style>
